import { createContext, useCallback, useState, type ReactNode } from 'react';
import type { Folder, Tag } from '../../types/api';
import FolderEditModal from '../FolderEditModal/FolderEditModal';
import TagEditModal from '../TagEditModal/TagEditModal';

export interface SidebarItemModalsContextValue {
  editFolder: (folder: Folder) => void;
  editTag: (tag: Tag) => void;
}

// Default is a no-op so sidebar rows can render (and be unit-tested) without a
// provider; the real edit flow requires <SidebarItemModalsProvider>.
const noop = () => {};
export const SidebarItemModalsContext = createContext<SidebarItemModalsContextValue>({
  editFolder: noop,
  editTag: noop,
});

/**
 * Hosts a single shared Folder/Tag edit modal for the sidebar. Rows request an
 * edit via `useSidebarItemModals()` rather than each mounting their own modal.
 */
export function SidebarItemModalsProvider({ children }: { children: ReactNode }) {
  const [folderToEdit, setFolderToEdit] = useState<Folder | null>(null);
  const [tagToEdit, setTagToEdit] = useState<Tag | null>(null);

  const editFolder = useCallback((folder: Folder) => setFolderToEdit(folder), []);
  const editTag = useCallback((tag: Tag) => setTagToEdit(tag), []);

  return (
    <SidebarItemModalsContext.Provider value={{ editFolder, editTag }}>
      {children}
      {folderToEdit && (
        <FolderEditModal
          isOpen
          folder={folderToEdit}
          onClose={() => setFolderToEdit(null)}
        />
      )}
      {tagToEdit && (
        <TagEditModal isOpen tag={tagToEdit} onClose={() => setTagToEdit(null)} />
      )}
    </SidebarItemModalsContext.Provider>
  );
}

export default SidebarItemModalsProvider;
